<template lang="pug">
  .b-node(
    v-if="item && !isGroup"
    :class="{'_drag-over': isDragOver, '_not-in-group': !isInGroup}"
    :style="alignPosition"
  )
    .b-node__content(
      :class="{'_drag-over': isDragOver}"
      :style="contentStyle"
      :draggable="isDraggable"
      @dragstart="dragStart"
      @dragend="dragEnd"
      @dragover="dragOver"
      @dragenter="dragEnter"
      @dragleave="dragLeave"
    )
      .b-node__content-inner(
        ref="content"
        :class="contentInnerClasses"
        :data-connector-from="item.id"
        :data-connector-to="!isInGroup && item.id"
        :data-position-from="isInGroup && item.id"
        :data-position-to="!isInGroup && item.id"
        :data-position-container-id="positionContainerId"
      )
        NodeHead(
          v-if="!isInGroup"
          :item="item"
          :audioAllowed="audioAllowed"
        )

        // Content for node in a group
        template(v-if="item._editableText")
          NodeSelect(
            v-if="item.type === ELEMENTS_TYPES.CHANGE_RECOGNITION_LANG"
            :item="item"
          )
          NodeEditText(
            v-else
            :class="{'-hide-parent': isInGroup}"
            :item="item"
            :isDefault="isDefault"
          )
            .b-node__button-outer(v-if="isInGroup")
              NodeDeleteButton.-hide.-mr-1(
                v-if="!isDefault"
                :id="item.id"
                :key="draggingItem && draggingItem.id"
                :class="{'-hide-persist': draggingItem && draggingItem.id}"
              )

        template(
          v-if="!allowDragScroll"
        )
          // Dropzones
          .b-node__dropzone(
            v-if="item._allowDropInTree || item._allowDropInGroup"
            :class="{'_drag-over': isDragOver}"
            draggable
            @drop="drop"
          )
          // Menu
          NodeAddMenu(
            v-if="allowAddMenu"
            :key="item._allowDropInTree"
            :item="item"
            :hasConnection="hasConnection"
            :isDragOver="isDragOver"
          )
    .b-node__children(
      v-if="hasChildren"
      ref="child"
    )
      Node(
        v-for="(node, nodeIndex) in _children"
        :key="`${node.id}_${node._childrenCount}`"
        :item="node"
        :isDeleted="isDeleted"
      )
  .b-group-block(
    v-else-if="item && isGroup"
  )
    .b-group-outer(
      :data-connector-to="!isInGroup && item.id"
      :data-position-from-container="item.id"
      :style="{transform: `scale(${layoutScale})`}"
    )
      NodesGroup(
        :item="item"
        :amount="Array.isArray(_children) && _children.length"
      )
        Node(
          v-if="Array.isArray(_children)"
          v-for="(node, nodeIndex) in _children"
          :key="nodeIndex + node.id"
          :item="node"
          :isDefault="nodeIndex === 0"
          :isInGroup="true"
          :isDeleted="isDeleted"
        )
    NodesGroupChildren(
      :data-position-to-container="item.id"
    )
      Node(
        v-if="Array.isArray(_grandsons)"
        v-for="(node, nodeIndex) in _grandsons"
        :key="nodeIndex + node.id"
        :item="node"
        :positionContainerId="item.id"
        :data-position-to-outer="node.id"
        :isDeleted="isDeleted"
      )
</template>

<script>
import NodeHead from '@/components/node/NodeHead';
import NodeDeleteButton from '@/components/node/NodeDeleteButton';
import NodeSelect from '@/components/node/NodeSelect';
import NodeEditText from '@/components/node/NodeEditText';
import NodeAddMenu from '@/components/node/NodeAddMenu';
import NodesGroup from '@/components/nodes/NodesGroup';
import NodesGroupChildren from '@/components/nodes/NodesGroupChildren';
import drag from '@/mixims/drag';
import { mapActions, mapGetters } from 'vuex';
import { mapFields } from 'vuex-map-fields';
import {
  ELEMENTS_TYPES,
  ELEMENTS_TYPES_ALLOWED_RECORD,
  ELEMENTS_TYPES_GROUPS,
  ELEMENTS_TYPES_LABELS,
} from '@/api/elements';
import objectValue from '@/utils/objectValue';

export default {
  name: 'Node',
  mixins: [
    drag,
  ],
  props: {
    item: Object,
    isInGroup: {
      type: Boolean,
      default: false,
    },
    positionContainerId: String,
    isDefault:  {
      type: Boolean,
      default: false,
    },
    isDeleted:  {
      type: Boolean,
      default: false,
    },
  },
  components: {
    NodeHead,
    NodeSelect,
    NodeEditText,
    NodeDeleteButton,
    NodeAddMenu,
    NodesGroup,
    NodesGroupChildren,
  },
  data: () => ({
    isDragging: false,
    ELEMENTS_TYPES,
    ELEMENTS_TYPES_LABELS,
  }),
  computed: {
    ...mapFields('scroll', [
      'allowDragScroll',
      'isDragScroll',
    ]),
    ...mapFields('visualPositions', [
      'itemsPositions',
    ]),
    ...mapFields('nodes', [
      'date',
      'draggingItem',
      'draggedOverId',
      'editingNodeText',
      'focusId',
    ]),
    ...mapGetters('nodes', [
      'getItemsChildren',
      'getItemsGrandsons',
    ]),
    ...mapFields('scale', [
      'layoutScale',
    ]),
    _children() {
      return !Object.values(ELEMENTS_TYPES_GROUPS).includes(this.item.type)
        && this.getItemsChildren(this.item.id);
    },
    _grandsons() {
      return this.getItemsGrandsons(this.item.id);
    },
    hasChildren() {
      return Array.isArray(this._children) && this._children.length > 0;
    },
    hasConnection() {
      const children = this.getItemsChildren(this.item.id);
      return Array.isArray(children) && children.length > 0;
    },
    isDraggable() {
      return !this.editingNodeText
        && !this.isDefault
        && !this.allowDragScroll
        && !this.item.isRoot;
    },
    isDragOver() {
      return this.draggedOverId === this.item.id;
    },
    allowAddMenu() {
      return !this.isDeleted && !this.isDragging;
    },
    audioAllowed() {
      return ELEMENTS_TYPES_ALLOWED_RECORD.includes(this.item.type);
    },
    isGroup() {
      return Object.keys(ELEMENTS_TYPES_GROUPS).includes(this.item.type);
    },
    contentStyle() {
      return {
        transform: !this.isInGroup && `scale(${this.layoutScale})`,
      };
    },
    alignPosition() {
      const item = objectValue(this.itemsPositions, this.item.id);
      if (!item) {
        return false;
      }

      const diff = item.adjustY + item.addTop;
      return item && {
        transform: `translateY(${diff}px)`,
      };
    },
    contentInnerClasses() {
      const { item, layoutScale, isDraggable, isInGroup, focusId } = this;
      return [
        `_type-${item.type}`,
        {
          _narrow: !item._editableText,
          _draggable: isDraggable,
          '_not-in-group': !isInGroup,
          _focus: item.id === focusId,
        },
      ];
    },
  },
  methods: {
    ...mapActions('nodes', [
      'addNode',
      'swapNodes',
    ]),
  },
};
</script>
